import { STATUS } from "./constants";
import { Option } from "./types";
import { 
  CotizationDetail, 
  Reservation, 
  ReservationTable, 
  CotizationWithPrice, 
  AerolinePriceObject, 
  HotelPriceObject, 
  DestinationWithAerolinesAndHotelsTable, 
  DestinationWithAerolinesAndHotelsObject, 
  PlaceAerolineTable, 
  PlaceAerolineObject, 
  PlaceHotelTable, 
  PlaceHotelObject, 
  TourObject, 
  TourTable 
} from "./interfaces";

export const formatClients = (data: any) => {
  return data?.map((element: any) => ({
    ...element,
    reservations: element.reservations ? element.reservations[0]?.count : 0,
  }));
};

export const formatHotels = (data: any) => {
  return data?.map(({ reservations_hotels, places_hotels, ...element }: any) => ({
    ...element,
    reservations: reservations_hotels ? reservations_hotels[0]?.count : 0,
    destinations: places_hotels
      ? places_hotels.map((ph: any) => ph.places).filter(Boolean)
      : [],
  }));
};

export const formatCotizationToDatabase = (
  cotization: CotizationDetail
): Reservation => {
  const {
    startDate,
    adultFee,
    endDate,
    infantFee,
    customer,
    kidFee,
    traslado,
    days,
    placeId,
    tours,
    status,
    assistanceCardAdult,
    assistanceCardChild,
  } = cotization;

  return {
    days,
    places_id: placeId,
    start_date: startDate,
    end_date: endDate,
    tours: tours || {},
    fee_adults: adultFee || 0,
    fee_kids: kidFee,
    fee_infants: infantFee,
    relocation: traslado,
    customer_id: customer,
    status,
    assistance_card_adult: assistanceCardAdult || 0,
    assistance_card_child: assistanceCardChild || 0,
  };
};

export const formatAerolineReservationToDatabase = (reservation: any) => {
  const { priceByAdults, priceByKids, id, name, table_id, price, ...rest } =
    reservation;

  return {
    ...rest,
    price,
    id: table_id,
    adult_price: priceByAdults > 0 ? parseInt(priceByAdults) : 0,
    kid_price: priceByKids > 0 ? parseInt(priceByKids) : 0,
  };
};

export const formatHotelReservationToDatabase = (reservation: any) => {
  const { priceByAdults, id, priceByKids, name, table_id, price, ...rest } =
    reservation;

  return {
    ...rest,
    price,
    id: table_id,
    adult_price: priceByAdults > 0 ? parseInt(priceByAdults) : 0,
    kid_price: priceByKids > 0 ? parseInt(priceByKids) : 0,
  };
};

export const formatReservationWithDestinations = (
  data: ReservationTable
): CotizationWithPrice => {
  const {
    places,
    fee_infants,
    reservation_rooms,
    reservations_hotels,
    reservations_aerolines,
    reservations_tours,
    start_date,
    end_date,
    fee_adults,
    fee_kids,
    clients,
    relocation,
    traslado,
    tours,
    id,
    days,
    customer_id,
    places_id,
    assistance_card_adult,
    assistance_card_child,
    ...element
  } = data;
  return {
    id,
    cotizationDetail: {
      ...element,
      days,
      place: places?.name,
      places: places,
      placeImage: places?.cover_image_url || places?.image_url || null,
      destinationImage: places?.cover_image_url || places?.image_url || null,
      startDate: start_date,
      tours,
      endDate: end_date,
      traslado: traslado ?? relocation,
      adultFee: fee_adults,
      infantFee: fee_infants,
      kidFee: fee_kids,
      customer: customer_id,
      placeId: places_id,
      customerName: clients?.name,
      rooms: reservation_rooms || [],
      assistanceCardAdult: assistance_card_adult,
      assistanceCardChild: assistance_card_child,
      reservationTours: reservations_tours || [],
    },
    aerolinePrices: (reservations_aerolines || [])
      .filter((ra: any) => ra.aerolines != null)
      .reduce((acc: any[], curr: any) => {
        const existing = acc.find(item => item.id === curr.aerolines.id && item.airlineConfigId === curr.airline_configuration_id);
        if (!existing) {
          acc.push({
            price: curr.price,
            table_id: curr.id,
            id: curr.aerolines.id,
            name: curr.airline_configurations?.flight_types?.name ? `${curr.aerolines.name} - ${curr.airline_configurations.flight_types.name}` : curr.aerolines.name,
            priceByAdults: curr.adult_price,
            priceByKids: curr.kid_price || 0,
            infantPrice: curr.infant_price || 0,
            airlineConfigId: curr.airline_configuration_id,
            configName: curr.airline_configurations?.flight_types?.name,
          });
        }
        return acc;
      }, []),
    hotelPrices: (reservations_hotels || [])
      .filter((rh: any) => rh.hotels != null)
      .reduce((acc: any[], curr: any) => {
        const existing = acc.find(item => item.id === curr.hotels.id && item.hotelConfigId === curr.hotel_configuration_id);
        if (!existing) {
          acc.push({
            price: curr.price,
            table_id: curr.id,
            id: curr.hotels.id,
            name: curr.hotel_configurations?.room_types?.name ? `${curr.hotels.name} - ${curr.hotel_configurations.room_types.name}` : curr.hotels.name,
            priceByAdults: curr.adult_price,
            priceByKids: curr.kid_price || 0,
            toddlerPrice: curr.toddler_price || 0,
            infantPrice: curr.infant_price || 0,
            hotelConfigId: curr.hotel_configuration_id,
            configName: curr.hotel_configurations?.room_types?.name,
            isNonRefundable: curr.hotel_configurations?.is_non_refundable,
            mealPlan: curr.hotel_configurations?.meal_plan,
          });
        }
        return acc;
      }, []),
  };
};

export const getNewPrices = (
  newArray: Option[],
  options: Option[],
  prices: AerolinePriceObject[] | HotelPriceObject[]
): AerolinePriceObject[] | HotelPriceObject[] => {
  const newArrayWithId = newArray.filter((e) => e).map((e) => e.value);

  const setSelected = new Set(newArrayWithId);
  const setPrevious = new Set(prices.map((e) => e.id));

  const intersection = new Set(
    [...setSelected].filter((x) => setPrevious.has(Number(x)))
  );
  let newPrices = prices.filter((aeroline) =>
    [...intersection].find((e) => parseInt(e) === aeroline.id)
  );

  const difference = new Set(
    [...setSelected].filter((x) => !setPrevious.has(Number(x)))
  );
  let differenceArray = Array.from(difference);

  let differenceOptions = differenceArray.map((e) => {
    const foundPrice = prices.find((price) => price.id === parseInt(e));
    const foundOption = options.find((option) => option.value === e);
    if (foundPrice) return foundPrice;
    return {
      id: foundOption?.value ? parseInt(foundOption.value) : 0,
      price: 0,
      name: foundOption?.label || "",
      table_id: 0,
      priceByAdults: 0,
      priceByKids: 0,
    };
  });

  if (differenceOptions.length > 0) {
    newPrices.push(...differenceOptions);
  }

  return newPrices;
};

export const formatPlaceWithAerolineAndHotelToObject = (
  place: DestinationWithAerolinesAndHotelsTable | any
): DestinationWithAerolinesAndHotelsObject | any => {
  const { id, name, country, code, airports, places_hotels, places_aerolines, places_operators, tours, image_url, cover_image_url, slogan, info_pdf_url, info_pdfs, services, status, notes, seasons, travel_requirements } = place;
  
  let formattedInfoPdfs = info_pdfs;
  if ((!formattedInfoPdfs || formattedInfoPdfs.length === 0) && info_pdf_url) {
    formattedInfoPdfs = [{ title: 'PDF Informativo', url: info_pdf_url }];
  } else if (!formattedInfoPdfs) {
    formattedInfoPdfs = [];
  }

  return {
    id,
    country,
    name,
    code: code || null,
    airports: Array.isArray(airports) ? airports : null,
    image_url,
    cover_image_url,
    slogan,
    info_pdf_url,
    info_pdfs: formattedInfoPdfs,
    services,
    status,
    notes,
    seasons,
    travel_requirements,
    aerolines: places_aerolines ? places_aerolines.map((e: any) => formatPlaceAerolineToObject(e)) : [],
    hotels: places_hotels ? places_hotels.map((e: any) => formatPlaceHotelToObject(e)) : [],
    operators: places_operators ? places_operators.map((e: any) => formatPlaceOperatorToObject(e)) : [],
    tours,
  };
};

export const formatPlaceOperatorToObject = (
  place_operator: any
): any => {
  const { id, providers_id, providers, places_id } = place_operator;

  let newObject: any = {
    id,
    placeId: places_id,
    tableId: providers_id,
  };

  if (providers) {
    newObject["name"] = providers.name;
    newObject["logo_url"] = providers.logo_url || null;
    newObject["ruc"] = providers.ruc || null;
    newObject["razon_social"] = providers.razon_social || null;
    newObject["provider_type"] = providers.provider_type || null;
  }

  return newObject;
};

export const formatPlaceAerolineToObject = (
  place_aeroline: PlaceAerolineTable
): PlaceAerolineObject => {
  const { id, aerolines_id, aerolines, places_id } = place_aeroline;

  let newObject: PlaceAerolineObject = {
    id,
    placeId: places_id,
    tableId: aerolines_id,
  };

  if (aerolines) {
    newObject["name"] = aerolines.name;
    newObject["logo_url"] = aerolines.logo_url || null;
    // Pass airline_configurations through if they exist on the aerolines join
    if ((aerolines as any).airline_configurations) {
      newObject["airline_configurations"] = (aerolines as any).airline_configurations;
    }
  }

  return newObject;
};

export const formatPlaceHotelToObject = (
  place_hotel: PlaceHotelTable
): PlaceHotelObject => {
  const { id, hotels_id, hotels, places_id, stars } = place_hotel;

  let newObject: PlaceHotelObject = {
    id,
    placeId: places_id,
    tableId: hotels_id,
    hotelId: hotels_id,
  };

  if (hotels) {
    newObject["name"] = hotels.name;
    newObject["stars"] = (hotels as any).stars || stars;
    newObject["description"] = (hotels as any).description || null;
    newObject["main_image_url"] = (hotels as any).main_image_url || null;
    newObject["gallery_urls"] = (hotels as any).gallery_urls || [];
    // Pass hotel_configurations through if they exist on the hotels join
    // Filter out inactive (soft-deleted) configurations so downstream components
    // never display stale room types (e.g. an old "Standard NR" replaced by "Doble estándar")
    if ((hotels as any).hotel_configurations) {
      newObject["hotel_configurations"] = (hotels as any).hotel_configurations.filter(
        (c: any) => c.is_active !== false
      );
    }
  }
  if (stars && !newObject["stars"]) newObject["stars"] = stars;

  return newObject;
};

export const formatTourToObject = (tour: any): TourObject => {
  const { id, places_id, name, places, kidPrice, adultPrice, operators_id, providers, min_age_kid, max_age_kid, duration, category_id, image_url, gallery_urls, description, operating_days, service_type, itinerary, includes, not_includes, notes, tour_categories } = tour;

  return {
    id,
    placeId: places_id,
    placeName: places?.name,
    kidPrice,
    adultPrice,
    minAgeKid: min_age_kid || 3,
    maxAgeKid: max_age_kid || 12,
    name,
    operatorId: operators_id,
    operatorName: providers?.name,
    duration: duration || 'full_day',
    categoryId: category_id,
    categoryName: tour_categories?.name,
    categoryIcon: tour_categories?.icon,
    imageUrl: image_url,
    galleryUrls: gallery_urls || [],
    description,
    operatingDays: operating_days,
    serviceType: service_type,
    itinerary,
    includes,
    notIncludes: not_includes,
    notes,
  };
};

export const formatTourToTable = (tour: TourObject): TourTable => {
  const { placeId, name, kidPrice, adultPrice, minAgeKid, maxAgeKid, operatorId, duration, categoryId, imageUrl, galleryUrls, description, operatingDays, serviceType, itinerary, includes, notIncludes, notes } = tour;
  let newObject: TourTable = {
    places_id: placeId,
    name,
    kidPrice,
    adultPrice,
    min_age_kid: minAgeKid || 3,
    max_age_kid: maxAgeKid || 12,
    operators_id: operatorId,
    duration: duration || 'full_day',
    category_id: categoryId || null,
    image_url: imageUrl || null,
    gallery_urls: galleryUrls || [],
    description,
    operating_days: operatingDays,
    service_type: serviceType,
    itinerary,
    includes,
    not_includes: notIncludes,
    notes,
  };

  if (tour.id) newObject["id"] = tour.id;

  return newObject;
};

export const formatTours = (tours: any) => {
  return Object.entries(tours).reduce((acc: any, [id, tour]) => {
    if (tour !== null) {
      acc[id] = tour;
    }
    return acc;
  }, {});
};

export const convertTourFormat = (originalTours: any) => {
  if (!originalTours) return null;
  if (Object.keys(originalTours).length !== 0) {
    const convertedTours = Object.entries(originalTours)
      .map(([id, tour]) => {
        if (tour) {
          return { id, ...tour };
        }

        return null;
      })
      .filter((tour: any) => tour?.adultPrice > 0);
    return convertedTours;
  }

  return [];
};

export const totalOperativity = (
  minAerolinePrice: string,
  hotelPrice: string,
  traslado: string,
  tourPrices: string
) => {
  return (
    (!isNaN(parseInt(minAerolinePrice)) ? parseInt(minAerolinePrice) : 0) +
    (!isNaN(parseInt(traslado)) ? parseInt(traslado) : 0) +
    (!isNaN(parseInt(hotelPrice)) ? parseInt(hotelPrice) : 0) +
    (!isNaN(parseInt(tourPrices)) ? parseInt(tourPrices) : 0)
  );
};

export const getColorByStatus = (status: string) => {
  switch (status) {
    case STATUS.COTIZADO:
      return "bg-slate-500";
    case STATUS.PENDIENTE:
      return "bg-orange-500";
    case STATUS.RESERVADO:
      return "bg-red-500";
    case STATUS["EN CURSO"]:
      return "bg-blue-500";
    case STATUS.FINALIZADO:
      return "bg-green-800";
    default:
      return "bg-slate-500";
  }
};

export const getTotalPriceByHotels = (
  hotels: any,
  aerolinePrice: number,
  adultTraslado: number,
  kidTraslado: number,
  tours: any,
  adultFee: number,
  kidFee: number
) => {
  const tourAdultPrice = tours?.reduce(
    (acc: number, tour: any) => tour.adultPrice + acc,
    0
  );
  const tourKidPrice = tours?.reduce(
    (acc: number, tour: any) => tour.kidPrice + acc,
    0
  );

  const adultsPrices = [aerolinePrice, adultTraslado, tourAdultPrice].reduce(
    (acc, current) => acc + current,
    0
  );
  const kidsPrices = [aerolinePrice, kidTraslado, tourKidPrice].reduce(
    (acc, current) => acc + current,
    0
  );
  // (9-(number % 10)) + number
  return hotels.map(({ name, priceByAdults, priceByKids }: any) => {
    const totalAdults = Math.round(
      priceByAdults + adultsPrices + adultFee * 1.18
    );
    const totalKids = Math.round(priceByKids + kidsPrices + kidFee * 1.18);
    const tourServices = tours.map((tour: any) => ({
      name: tour.name,
      adultPrice: tour.adultPrice,
      kidPrice: tour.kidPrice,
    }));

    return {
      name: name,
      services: [
        {
          name: "Boleto aereo",
          adultPrice: aerolinePrice,
          kidPrice: aerolinePrice,
        },
        {
          name,
          adultPrice: priceByAdults,
          kidPrice: priceByKids,
        },
        {
          name: "Traslado Compartido",
          adultPrice: adultTraslado,
          kidPrice: kidTraslado,
        },
        ...tourServices,
        {
          name: "Sub Total",
          adultPrice: priceByAdults + adultsPrices,
          kidPrice: priceByKids + kidsPrices,
        },
        {
          name: "Fee",
          adultPrice: adultFee,
          kidPrice: kidFee,
        },
        {
          name: "IGV (Sobre Fee)",
          adultPrice: (adultFee * 0.18).toFixed(2),
          kidPrice: (kidFee * 0.18).toFixed(2),
        },
        {
          name: "Total",
          adultPrice: priceByAdults + adultsPrices + adultFee * 1.18,
          kidPrice: priceByKids + kidsPrices + kidFee * 1.18,
        },
      ],
      totalByAdults: 9 - (totalAdults % 10) + totalAdults,
      totalByKids: 9 - (totalKids % 10) + totalKids,
    };
  });
};

export const calculateDays = (startDate: string, endDate: string): number => {
  if (!startDate || !endDate) return 0;

  const start: Date = new Date(startDate);
  const end: Date = new Date(endDate);

  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    return 0;
  }

  const diffTime: number = Math.abs(end.getTime() - start.getTime());

  let res = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return res + 1;
};

export type PayloadCostPrepared = {
  adultCost: number;
  kidCost: number;
  toddlerCost: number;
};

export const prepareHotelPayload = (
  totalAdults: number,
  totalKids: number,
  totalToddler: number,
  totalCost: number
): PayloadCostPrepared => {
  const denominator = 2 * totalAdults + totalKids + 0.8 * totalToddler;

  let kidCost = 0;
  let adultCost = 0;
  let toddlerCost = 0;

  if (denominator > 0 && totalCost > 0) {
    const rawKidCost = totalCost / denominator;
    kidCost = Math.round(rawKidCost * 100) / 100;
    adultCost = Math.round(2 * rawKidCost * 100) / 100;
    toddlerCost = Math.round(0.8 * rawKidCost * 100) / 100;
  }

  return {
    adultCost,
    kidCost: totalKids > 0 ? kidCost : 0,
    toddlerCost: totalToddler > 0 ? toddlerCost : 0,
  };
};

export const prepareAerolinePayload = (
  totalAdults: number,
  totalKids: number,
  totalToddler: number,
  totalCost: number
): PayloadCostPrepared => {
  const denominator = totalAdults + totalKids + totalToddler;

  let kidCost = 0;
  let adultCost = 0;
  let toddlerCost = 0;

  if (denominator > 0 && totalCost > 0) {
    const averageCost = Math.round((totalCost / denominator) * 100) / 100;
    adultCost = averageCost;
    kidCost = averageCost;
    toddlerCost = averageCost;
  }

  return {
    adultCost,
    kidCost: totalKids > 0 ? kidCost : 0,
    toddlerCost: totalToddler > 0 ? toddlerCost : 0,
  };
};


export const getRefId = (entity: any): string => {
  if (!entity) return "";
  const idValue = typeof entity === 'string' ? entity : (entity.reservations?.id || entity.id);
  if (!idValue) return "";
  return String(idValue).slice(0, 8).toUpperCase();
};

